import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/admin.css';

const AdminMasajistas = () => {
    const { user } = useAuth();
    const [masajistas, setMasajistas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [editingMasajista, setEditingMasajista] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        apellido: '',
        email: '',
        password: '',
        telefono: '',
        domicilio: '',
        dni: ''
    });
    const [formErrors, setFormErrors] = useState({});

    // Cargar masajistas
    useEffect(() => {
        const fetchMasajistas = async () => {
            try {
                const res = await api.get('/admin/masajistas');
                setMasajistas(res.data);
            } catch (error) {
                console.error('Error al cargar masajistas:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchMasajistas();
    }, []);

    // Filtrar
    const filtered = masajistas.filter(m => {
        const matchesSearch =
            m.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.dni?.includes(searchTerm);
        const matchesStatus = filterStatus === 'all' || m.active === (filterStatus === 'activo');
        return matchesSearch && matchesStatus;
    });

    // Estadísticas
    const total = masajistas.length;
    const activos = masajistas.filter(m => m.active).length;
    const inactivos = total - activos;

    // Handlers del formulario
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.nombre.trim()) errors.nombre = 'El nombre es requerido';
        if (!formData.apellido.trim()) errors.apellido = 'El apellido es requerido';
        if (!formData.email.trim()) errors.email = 'El email es requerido';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email inválido';
        if (!editingMasajista && !formData.password) errors.password = 'La contraseña es requerida';
        if (formData.password && formData.password.length < 6) errors.password = 'Mínimo 6 caracteres';
        if (!formData.telefono.trim()) errors.telefono = 'El teléfono es requerido';
        if (!formData.domicilio.trim()) errors.domicilio = 'El domicilio es requerido';
        if (!formData.dni.trim()) errors.dni = 'El DNI es requerido';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const resetForm = () => {
        setFormData({
            nombre: '',
            apellido: '',
            email: '',
            password: '',
            telefono: '',
            domicilio: '',
            dni: ''
        });
        setFormErrors({});
        setEditingMasajista(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const payload = { ...formData };
            if (!editingMasajista) {
                const res = await api.post('/admin/masajistas', payload);
                setMasajistas(prev => [...prev, res.data]);
            } else {
                if (!payload.password) delete payload.password;
                const res = await api.put(`/admin/masajistas/${editingMasajista._id}`, payload);
                setMasajistas(prev =>
                    prev.map(m => (m._id === editingMasajista._id ? res.data : m))
                );
            }
            setShowModal(false);
            resetForm();
        } catch (error) {
            console.error('Error al guardar masajista:', error);
            alert(error.response?.data?.message || 'Error al guardar');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este masajista permanentemente?')) return;
        try {
            await api.delete(`/admin/masajistas/${id}`);
            setMasajistas(prev => prev.filter(m => m._id !== id));
        } catch (error) {
            console.error('Error al eliminar:', error);
            alert('Error al eliminar');
        }
    };

    const toggleActive = async (id, currentActive) => {
        try {
            const res = await api.patch(`/admin/masajistas/${id}/status`, { active: !currentActive });
            setMasajistas(prev =>
                prev.map(m => (m._id === id ? res.data : m))
            );
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            alert('Error al cambiar estado');
        }
    };

    const openEdit = (masajista) => {
        setEditingMasajista(masajista);
        setFormData({
            nombre: masajista.nombre || '',
            apellido: masajista.apellido || '',
            email: masajista.email || '',
            password: '',
            telefono: masajista.telefono || '',
            domicilio: masajista.domicilio || '',
            dni: masajista.dni || ''
        });
        setShowModal(true);
    };

    const openNew = () => {
        resetForm();
        setShowModal(true);
    };

    // Render helpers
    const getInitials = (nombre, apellido) =>
        `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();

    const getStatusBadge = (active) => (
        <span className={`estado-badge ${active ? 'active' : 'inactive'}`}>
            <span className={`estado-dot ${active ? 'active' : 'inactive'}`}></span>
            {active ? 'Activo' : 'Inactivo'}
        </span>
    );

    // Vistas grid y list
    const renderGrid = () => (
        <div className="clients-grid">
            {filtered.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">🧘</div>
                    <div className="empty-state-title">No hay masajistas</div>
                    <div className="empty-state-text">Agrega un nuevo masajista usando el botón "+ Nuevo"</div>
                </div>
            ) : (
                filtered.map(m => (
                    <div key={m._id} className="client-card">
                        <div className="client-card-header">
                            <div className="client-avatar">{getInitials(m.nombre, m.apellido)}</div>
                            <div className="client-header-info">
                                <div className="client-name">{m.nombre} {m.apellido}</div>
                                <div className="client-email">{m.email}</div>
                            </div>
                            <div>{getStatusBadge(m.active)}</div>
                        </div>
                        <div className="client-card-body">
                            <div className="client-info-row">📱 {m.telefono}</div>
                            <div className="client-info-row">🏠 {m.domicilio}</div>
                            <div className="client-info-row">🆔 DNI: {m.dni}</div>
                            <div className="client-info-row">
                                🗓️ <span className="link-like" onClick={() => alert('Ver calendario de turnos')}>
                                    Calendario
                                </span>
                            </div>
                        </div>
                        <div className="client-card-footer">
                            <div className="btn-group-actions">
                                <button className="btn-client-action btn-edit" onClick={() => openEdit(m)}>
                                    ✏️ Editar
                                </button>
                                <button
                                    className={`btn-client-action ${m.active ? 'btn-inactive' : 'btn-active'}`}
                                    onClick={() => toggleActive(m._id, m.active)}
                                >
                                    {m.active ? '🔒 Desactivar' : '✅ Activar'}
                                </button>
                                <button className="btn-client-action btn-delete" onClick={() => handleDelete(m._id)}>
                                    🗑️ Eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const renderList = () => (
        <div className="clients-list">
            {filtered.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">🧘</div>
                    <div className="empty-state-title">No hay masajistas</div>
                </div>
            ) : (
                filtered.map(m => (
                    <div key={m._id} className="client-list-item">
                        <div className="client-list-info">
                            <div className="client-list-header">
                                <div className="client-list-avatar">{getInitials(m.nombre, m.apellido)}</div>
                                <div className="client-list-details">
                                    <div className="client-list-name">{m.nombre} {m.apellido}</div>
                                    <div className="client-list-contact">
                                        <span>📧 {m.email}</span>
                                        <span>📱 {m.telefono}</span>
                                        <span>🏠 {m.domicilio}</span>
                                        <span>🆔 {m.dni}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="client-list-status">{getStatusBadge(m.active)}</div>
                        <div className="client-list-actions">
                            <button className="btn-client-action btn-edit" onClick={() => openEdit(m)}>✏️</button>
                            <button
                                className={`btn-client-action ${m.active ? 'btn-inactive' : 'btn-active'}`}
                                onClick={() => toggleActive(m._id, m.active)}
                            >
                                {m.active ? '🔒' : '✅'}
                            </button>
                            <button className="btn-client-action btn-delete" onClick={() => handleDelete(m._id)}>🗑️</button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    return (
        <div className="admin-dashboard">
            <div style={{ padding: '1.5rem 2rem' }}>
                
                {/* ESTADÍSTICAS COMPACTAS (Estilo Reportes) */}
                <div className="stats-compact">
                    <div className="stat-card-compact primary">
                        <div className="stat-icon">👥</div>
                        <div className="stat-content">
                            <div className="stat-value">{total}</div>
                            <div className="stat-label">Total Masajistas</div>
                        </div>
                    </div>
                    
                    <div className="stat-card-compact success">
                        <div className="stat-icon">✅</div>
                        <div className="stat-content">
                            <div className="stat-value">{activos}</div>
                            <div className="stat-label">Activos</div>
                        </div>
                    </div>
                    
                    <div className="stat-card-compact danger">
                        <div className="stat-icon">🚫</div>
                        <div className="stat-content">
                            <div className="stat-value">{inactivos}</div>
                            <div className="stat-label">Inactivos</div>
                        </div>
                    </div>

                    {/* Botón Nuevo sin texto secundario */}
                    <div 
                        className="stat-card-compact info" 
                        style={{ cursor: 'pointer' }} 
                        onClick={openNew}
                    >
                        <div className="stat-icon">➕</div>
                        <div className="stat-content">
                            <div className="stat-value" style={{ fontSize: '1.1rem', marginTop: '0.2rem' }}>Nuevo</div>
                        </div>
                    </div>
                </div>

                {/* Sección de Masajistas */}
                <div className="clients-section">
                    <div className="section-header">
                        <div className="section-title">
                            👥 Lista de Masajistas
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>
                                ({filtered.length} mostrados)
                            </span>
                        </div>
                        <div className="section-actions">
                            <div className="search-box">
                                <span className="search-icon">🔍</span>
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Buscar por nombre, email o DNI..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="filter-group">
                                <button
                                    className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                                    onClick={() => setFilterStatus('all')}
                                >
                                    Todos
                                </button>
                                <button
                                    className={`filter-btn ${filterStatus === 'activo' ? 'active' : ''}`}
                                    onClick={() => setFilterStatus('activo')}
                                >
                                    ✅ Activos
                                </button>
                                <button
                                    className={`filter-btn ${filterStatus === 'inactivo' ? 'active' : ''}`}
                                    onClick={() => setFilterStatus('inactivo')}
                                >
                                    🚫 Inactivos
                                </button>
                            </div>
                            <div className="toggle-group">
                                <button
                                    className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                    onClick={() => setViewMode('grid')}
                                >
                                    ▦ Grid
                                </button>
                                <button
                                    className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => setViewMode('list')}
                                >
                                    ≡ Lista
                                </button>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="spinner-container"><div className="spinner"></div></div>
                    ) : (
                        viewMode === 'grid' ? renderGrid() : renderList()
                    )}
                </div>
            </div>

            {/* Modal de creación/edición */}
            {showModal && (
                <div className="modal-backdrop" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {editingMasajista ? 'Editar Masajista' : 'Nuevo Masajista'}
                            </h5>
                            <button className="btn-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.nombre ? 'is-invalid' : ''}`}
                                        name="nombre"
                                        placeholder="Nombre *"
                                        value={formData.nombre}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.nombre && <div className="invalid-feedback">{formErrors.nombre}</div>}
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.apellido ? 'is-invalid' : ''}`}
                                        name="apellido"
                                        placeholder="Apellido *"
                                        value={formData.apellido}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.apellido && <div className="invalid-feedback">{formErrors.apellido}</div>}
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="email"
                                        className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                                        name="email"
                                        placeholder="Email *"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="password"
                                        className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                                        name="password"
                                        placeholder={editingMasajista ? 'Contraseña (dejar en blanco para no cambiar)' : 'Contraseña * (mín. 6)'}
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        minLength={6}
                                    />
                                    {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.telefono ? 'is-invalid' : ''}`}
                                        name="telefono"
                                        placeholder="Teléfono *"
                                        value={formData.telefono}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.telefono && <div className="invalid-feedback">{formErrors.telefono}</div>}
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.domicilio ? 'is-invalid' : ''}`}
                                        name="domicilio"
                                        placeholder="Domicilio *"
                                        value={formData.domicilio}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.domicilio && <div className="invalid-feedback">{formErrors.domicilio}</div>}
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.dni ? 'is-invalid' : ''}`}
                                        name="dni"
                                        placeholder="DNI *"
                                        value={formData.dni}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {formErrors.dni && <div className="invalid-feedback">{formErrors.dni}</div>}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {editingMasajista ? 'Actualizar' : 'Crear Masajista'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMasajistas;